<template>
	<div class="sticky w-full top-full">
		<UFooter
			:ui="{
				container: 'hidden lg:hidden',
				root: [
					'transform transition-[height] duration-300 ease-in-out',
					collapsed ? 'sm:h-auto overflow-clip' : 'h-auto',
					locale === 'en' && collapsed ? 'h-56' : '',
					locale === 'es' && collapsed ? 'h-60' : ''
				]
			}"
		>
			<template #top>
				<UContainer>
					<div class="relative">
						<div class="flex items-center justify-between flex-row gap-4">
							<NuxtLink
								:to="localePath('/')"
								aria-label="Maverix Broadband"
							>
								<LogoFull class="w-auto h-10 sm:h-11 shrink-0 text-inverted" />
							</NuxtLink>
							<UButton
								v-if="loggedIn"
								:label="$t('footer.pay-bill')"
								target="_blank"
								:to="accountUrl"
								color="neutral"
								variant="outline"
								:aria-label="$t('footer.pay-bill')"
								size="xl"
							/>
						</div>
						<UButton
							:icon="collapsed ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
							:aria-label="collapsed ? $t('footer.expand') : $t('footer.collapse')"
							class="absolute -top-8 left-1/2 transform -translate-x-1/2 sm:hidden z-10"
							color="secondary"
							variant="ghost"
							@click="collapsed = !collapsed"
						/>
						<UFooterColumns
							:columns="columns"
							class="xl:justify-end"
						>
							<template #left>
								<div class="flex flex-col gap-4">
									<div class="flex items-center gap-2">
										<UButton
											to="https://www.google.com/maps/place/Maverix+Broadband/@39.4997395,-104.710539,11z/data=!4m6!3m5!1s0x6efa953aab4d9d79:0xf16f6e005df2538!8m2!3d39.4997395!4d-104.710539!16s%2Fg%2F11sxyfbj_4?sa=X&ved=2ahUKEwiHhJfJkIT-AhVCOH0KHfGbBMYQ_BJ6BQiqARAI&coh=164777&entry=tt&shorturl=1"
											target="_blank"
											icon="i-simple-icons-google"
											aria-label="Google"
											color="secondary"
											variant="ghost"
										/>
										<UButton
											to="https://nextdoor.com/pages/maverix-broadband-parker-co/"
											target="_blank"
											icon="i-custom-nextdoor"
											aria-label="Nextdoor"
											color="secondary"
											variant="ghost"
										/>
										<UButton
											to="https://www.facebook.com/maverixBB/"
											target="_blank"
											icon="i-simple-icons-facebook"
											aria-label="Facebook"
											color="secondary"
											variant="ghost"
										/>
									</div>

									<p class="text-sm max-w-xs">
										{{ $t('footer.providing') }}
									</p>

									<p class="text-sm text-inverted">
										{{ $t('footer.copyright') }} © Maverix Broadband, Inc. {{ new Date().getFullYear() }}.
										<NuxtLink
											:to="localePath('/tos')"
											class="underline underline-offset-2 decoration hover:decoration-2"
										>
											{{ $t('footer.tos') }}
										</NuxtLink>
										{{ $t('footer.and') }}
										<NuxtLink
											:to="localePath('/privacy')"
											class="underline underline-offset-2 decoration hover:decoration-2"
										>
											{{ $t('footer.privacy-policy') }}
										</NuxtLink>
									</p>
								</div>
							</template>
						</UFooterColumns>
					</div>
				</UContainer>
			</template>
		</UFooter>
	</div>
</template>

<script setup lang="ts">
import { useAuth } from '@/composables/useAuth'
import { useI18n } from 'vue-i18n'

const localePath = useLocalePath()
const {
	loggedIn, accountUrl
} = useAuth()

const {
	t, locale
} = useI18n({
	useScope: 'global'
})

const collapsed = ref(true)

const columns = computed(() => [
	{
		label: t('links.services'),
		children: [
			{
				label: t('links.residential'),
				to: localePath('/residential-internet')
			},
			{
				label: t('links.business'),
				to: localePath('/business-internet')
			},
			{
				label: t('links.service-areas'),
				to: localePath('/service-areas')
			},
			{
				label: t('links.installation'),
				to: localePath('/installation-overview')
			}
		]
	},
	{
		label: t('links.about'),
		children: [
			{
				label: t('links.about-us'),
				to: localePath('/about')
			},
			{
				label: t('links.blog'),
				to: localePath('/blog')
			},
			{
				label: t('links.fiber-news'),
				to: localePath('/press')
			},
			{
				label: t('links.careers'),
				to: localePath('/careers')
			}
		]
	},
	{
		label: t('links.support'),
		children: [
			...(loggedIn.value
				? [
					{
						label: t('links.your-account'), // only show if logged in
						to: accountUrl.value,
						target: '_blank'
					}
				]
				: []),
			{
				label: t('links.help-center'),
				to: 'https://help.maverixbroadband.com/en/',
				target: '_blank'
			},
			{
				label: t('links.contact-us'),
				to: localePath('/contact')
			}
		]
	}
])

watch(collapsed, newValue => {
	if (import.meta.client && !newValue) {
		// scroll to the bottom of the page when expanded with short delay
		setTimeout(() => {
			window?.scrollTo({
				top: document.body.scrollHeight,
				behavior: 'smooth'
			})
		}, 50)
	}
}, { immediate: true })
</script>
